<template>
  <div class="task-runs" v-loading="loading">
    <section class="page-hero">
      <div>
        <el-tag size="small" type="info" effect="plain">只读子模块记录</el-tag>
        <h1>运行记录</h1>
        <p>查看四个固定父任务下的真实子模块运行记录、完整运行编号、完整任务编号与完整批次编号。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="loadRuns(false)">刷新记录</el-button>
    </section>

    <el-alert
      class="scope-alert"
      type="info"
      :closable="false"
      show-icon
      title="当前展示的是子模块运行记录；父任务级运行聚合、部分成功和父任务回执尚未接入。"
    />

    <el-card class="panel contract-panel" shadow="never">
      <div class="contract-row">
        <div>
          <strong>运行记录只读合同</strong>
          <p>{{ contractVersion || '正在读取合同版本' }}</p>
        </div>
        <div class="contract-tags">
          <el-tag size="small" :type="contractReady ? 'success' : 'danger'">
            {{ contractReady ? '合同校验通过' : '合同未就绪' }}
          </el-tag>
          <el-tag size="small" type="info" effect="plain">子模块记录</el-tag>
          <el-tag size="small" :type="sideEffectsClean ? 'success' : 'danger'">
            {{ sideEffectsClean ? '无写入副作用' : '副作用校验失败' }}
          </el-tag>
        </div>
      </div>
    </el-card>

    <el-card class="panel filter-panel" shadow="never">
      <div class="filter-row">
        <el-select v-model="filters.group" placeholder="父任务" clearable size="small">
          <el-option label="市场事件监控" value="market_event_watch" />
          <el-option label="个股事件监控" value="stock_event_watch" />
          <el-option label="持仓上下文监控" value="position_context_watch" />
          <el-option label="系统健康检查" value="system_health_check" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态" clearable size="small">
          <el-option label="成功" value="success" />
          <el-option label="失败" value="failed" />
          <el-option label="执行中" value="running" />
          <el-option label="等待中" value="pending" />
        </el-select>
        <el-input
          v-model.trim="filters.batchLogId"
          size="small"
          clearable
          placeholder="完整批次编号（精确查询）"
          @keyup.enter.native="applyFilters"
        />
        <el-button size="small" type="primary" icon="el-icon-search" @click="applyFilters">查询</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="clearFilters">重置</el-button>
      </div>
      <p class="filter-note">筛选由后端只读合同执行；不使用旧运行记录接口，也不会在读取时自动修改历史状态。</p>
    </el-card>

    <el-card class="panel" shadow="never">
      <div slot="header" class="panel-head">
        <div>
          <strong>子模块运行记录</strong>
          <p>父任务名称仅用于归类，不代表已生成父任务级运行结果。</p>
        </div>
        <div class="page-size-control">
          <span>每页</span>
          <el-select v-model="pageSize" size="mini" @change="changePageSize">
            <el-option :value="20" label="20" />
            <el-option :value="50" label="50" />
            <el-option :value="100" label="100" />
          </el-select>
          <span>条</span>
        </div>
      </div>

      <el-table :data="runs" size="small" stripe empty-text="暂无子模块运行记录" @row-click="showDetail">
        <el-table-column label="完整运行编号" min-width="305">
          <template slot-scope="scope">
            <div class="full-id-cell">
              <span>{{ scope.row.run_id || '完整编号暂不可用' }}</span>
              <el-button v-if="scope.row.run_id" type="text" size="mini" @click.stop="copyText(scope.row.run_id, '运行编号')">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="完整批次编号" min-width="315">
          <template slot-scope="scope">
            <div class="full-id-cell">
              <span>{{ batchLogId(scope.row) }}</span>
              <el-button v-if="batchLogId(scope.row) !== '批次编号暂不可用'" type="text" size="mini" @click.stop="copyText(batchLogId(scope.row), '批次编号')">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="父任务" min-width="150">
          <template slot-scope="scope">
            <el-tag size="small" effect="plain" :type="groupTag(parentGroupId(scope.row))">
              {{ groupLabelById(parentGroupId(scope.row)) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="内部模块" width="105">
          <template slot-scope="scope">{{ moduleLabel(scope.row.task_type) }}</template>
        </el-table-column>
        <el-table-column label="运行时间" width="165">
          <template slot-scope="scope">{{ displayRunTime(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="scope">
            <el-tag size="mini" :type="statusType(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="新增" width="65">
          <template slot-scope="scope">{{ countText(scope.row.new_items_count) }}</template>
        </el-table-column>
        <el-table-column label="去重" width="65">
          <template slot-scope="scope">{{ countText(scope.row.deduped_count) }}</template>
        </el-table-column>
        <el-table-column label="通知" width="65">
          <template slot-scope="scope">{{ countText(scope.row.notified_count) }}</template>
        </el-table-column>
        <el-table-column label="监控范围" min-width="150">
          <template slot-scope="scope">{{ runScopeText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="结果摘要" min-width="220">
          <template slot-scope="scope">{{ runResultText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click.stop="showDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>{{ pageSummary }}</span>
        <div>
          <el-button size="small" :disabled="offset === 0 || loading" @click="previousPage">上一页</el-button>
          <el-button size="small" type="primary" :disabled="!hasMore || loading" @click="nextPage">下一页</el-button>
        </div>
      </div>
    </el-card>

    <el-drawer
      title="子模块运行详情"
      :visible.sync="showDetailDrawer"
      direction="rtl"
      size="760px"
      :append-to-body="true"
    >
      <div class="detail-body" v-loading="detailLoading">
        <el-alert
          type="info"
          :closable="false"
          show-icon
          title="此详情是单个内部模块的真实运行记录，不是父任务级聚合结果。"
        />

        <el-descriptions v-if="currentRun" class="detail-descriptions" :column="1" border size="small">
          <el-descriptions-item label="记录口径">子模块运行记录</el-descriptions-item>
          <el-descriptions-item label="完整运行编号">
            <div class="detail-id">
              <span>{{ currentRun.run_id || '完整编号暂不可用' }}</span>
              <el-button v-if="currentRun.run_id" type="text" size="mini" @click="copyText(currentRun.run_id, '运行编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="完整任务编号">
            <div class="detail-id">
              <span>{{ currentRun.task_id || '完整编号暂不可用' }}</span>
              <el-button v-if="currentRun.task_id" type="text" size="mini" @click="copyText(currentRun.task_id, '任务编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="完整批次编号">
            <div class="detail-id">
              <span>{{ batchLogId(currentRun) }}</span>
              <el-button v-if="batchLogId(currentRun) !== '批次编号暂不可用'" type="text" size="mini" @click="copyText(batchLogId(currentRun), '批次编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="所属父任务">{{ groupLabelById(parentGroupId(currentRun)) }}</el-descriptions-item>
          <el-descriptions-item label="内部模块">{{ moduleLabel(currentRun.task_type) }}</el-descriptions-item>
          <el-descriptions-item label="父任务运行编号">尚未建立</el-descriptions-item>
          <el-descriptions-item label="是否父任务结果">否</el-descriptions-item>
          <el-descriptions-item label="运行状态">
            <el-tag size="mini" :type="statusType(currentRun.status)">{{ statusText(currentRun.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="数据状态">{{ dataStatusText(currentRun.data_status || payloadValue(currentRun, 'data_status')) }}</el-descriptions-item>
          <el-descriptions-item label="新增 / 去重 / 通知">
            {{ countText(currentRun.new_items_count) }} / {{ countText(currentRun.deduped_count) }} / {{ countText(currentRun.notified_count) }}
          </el-descriptions-item>
          <el-descriptions-item label="监控范围">{{ runScopeText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="匹配股票">{{ matchedSymbolsText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="运行时间">{{ displayRunTime(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ displayTime(currentRun.completed_at) }}</el-descriptions-item>
          <el-descriptions-item label="结果摘要">{{ runResultText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item v-if="currentRun.error_message" label="错误信息">
            <span class="danger-text">{{ currentRun.error_message }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import scheduledTaskRunReadonlyApi from '@/api/scheduledTaskRunReadonly'

const EXPECTED_CONTRACT_PREFIX = 'scheduled_task_runs_readonly_contract_'

export default {
  name: 'ScheduledTaskRuns',
  data() {
    return {
      loading: false,
      detailLoading: false,
      runs: [],
      filters: { group: '', status: '', batchLogId: '' },
      offset: 0,
      pageSize: 20,
      hasMore: false,
      contractVersion: '',
      contractReady: false,
      sideEffectsClean: false,
      runSemantics: {},
      readOnlyEvidence: {},
      showDetailDrawer: false,
      currentRun: null
    }
  },
  computed: {
    currentPage() {
      return Math.floor(this.offset / this.pageSize) + 1
    },
    pageSummary() {
      return `第 ${this.currentPage} 页，本页 ${this.runs.length} 条${this.hasMore ? '，后续仍有记录' : '，已到末页'}`
    }
  },
  mounted() {
    this.loadRuns(false)
  },
  methods: {
    unwrapPayload(res) {
      return (res && (res.machine_payload || res.data || res.payload)) || res || {}
    },
    validateContract(payload) {
      const sideEffects = payload.side_effects || {}
      const sideEffectsClean = Object.keys(sideEffects).length > 0 && Object.values(sideEffects).every(value => value === false)
      const evidence = payload.read_only_evidence || {}
      const version = String(payload.contract_version || '')
      const ready = Boolean(
        payload.read_only === true &&
        payload.record_scope === 'child_task_run' &&
        version.startsWith(EXPECTED_CONTRACT_PREFIX) &&
        sideEffectsClean &&
        evidence.session_has_no_mutations === true
      )

      this.contractVersion = version
      this.contractReady = ready
      this.sideEffectsClean = sideEffectsClean
      this.runSemantics = payload.run_semantics || {}
      this.readOnlyEvidence = evidence
      return ready
    },
    async loadRuns(resetOffset = false) {
      if (resetOffset) this.offset = 0
      this.loading = true
      try {
        const params = {
          offset: this.offset,
          limit: this.pageSize
        }
        if (this.filters.group) params.group_id = this.filters.group
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.batchLogId) params.batch_log_id = this.filters.batchLogId

        const res = await scheduledTaskRunReadonlyApi.list(params)
        const payload = this.unwrapPayload(res)
        if (!this.validateContract(payload)) {
          this.runs = []
          this.hasMore = false
          throw new Error('运行记录只读合同校验未通过')
        }

        this.runs = Array.isArray(payload.runs) ? payload.runs : []
        this.hasMore = payload.has_more === true
      } catch (error) {
        this.runs = []
        this.hasMore = false
        this.$message.error(this.errorMessage(error, '加载子模块运行记录失败'))
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      this.loadRuns(true)
    },
    clearFilters() {
      this.filters = { group: '', status: '', batchLogId: '' }
      this.loadRuns(true)
    },
    changePageSize() {
      this.loadRuns(true)
    },
    previousPage() {
      if (this.offset === 0) return
      this.offset = Math.max(0, this.offset - this.pageSize)
      this.loadRuns(false)
    },
    nextPage() {
      if (!this.hasMore) return
      this.offset += this.pageSize
      this.loadRuns(false)
    },
    async showDetail(row) {
      this.currentRun = row
      this.showDetailDrawer = true
      if (!row || !row.run_id) return

      this.detailLoading = true
      try {
        const locator = row.run_locator || {
          task_id: row.task_id,
          task_type: row.task_type,
          batch_log_id: this.batchLogId(row)
        }
        const res = await scheduledTaskRunReadonlyApi.detail(row.run_id, locator)
        const payload = this.unwrapPayload(res)
        if (!this.validateContract(payload)) {
          throw new Error('运行详情只读合同校验未通过')
        }
        if (payload.run && typeof payload.run === 'object') this.currentRun = payload.run
      } catch (error) {
        this.$message.error(this.errorMessage(error, '加载运行详情失败'))
      } finally {
        this.detailLoading = false
      }
    },
    errorMessage(error, fallback) {
      const detail = error && error.response && error.response.data && error.response.data.detail
      if (detail && typeof detail === 'string') return detail
      if (detail && detail.message) return detail.message
      return (error && error.message) || fallback
    },
    copyText(text, label) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label}已复制`))
          .catch(() => this.$message.info(text))
      } else {
        this.$message.info(text)
      }
    },
    parentGroupId(row) {
      if (row && row.parent_group_id) return row.parent_group_id
      const taskType = row && row.task_type
      if (['announcement_watch', 'news_watch'].includes(taskType)) return 'market_event_watch'
      if (['stock_news_watch', 'irm_qa_watch'].includes(taskType)) return 'stock_event_watch'
      if (taskType === 'system_health_check') return 'system_health_check'
      return 'unmapped'
    },
    groupLabelById(groupId) {
      const map = {
        market_event_watch: '市场事件监控',
        stock_event_watch: '个股事件监控',
        position_context_watch: '持仓上下文监控',
        system_health_check: '系统健康检查'
      }
      return map[groupId] || '未识别任务'
    },
    groupTag(groupId) {
      const map = {
        market_event_watch: 'primary',
        stock_event_watch: 'success',
        position_context_watch: 'warning',
        system_health_check: 'info'
      }
      return map[groupId] || 'info'
    },
    moduleLabel(taskType) {
      const map = {
        announcement_watch: '公告',
        news_watch: '市场新闻',
        stock_news_watch: '个股新闻',
        irm_qa_watch: '董秘问答',
        system_health_check: '系统自检'
      }
      return map[taskType] || '未识别模块'
    },
    statusType(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return 'success'
      if (value === 'failed' || value === 'error') return 'danger'
      if (value === 'running' || value === 'pending') return 'warning'
      return 'info'
    },
    statusText(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'success') return '成功'
      if (value === 'failed' || value === 'error') return '失败'
      if (value === 'running') return '执行中'
      if (value === 'pending') return '等待中'
      return status || '未知'
    },
    dataStatusText(status) {
      const value = String(status || '').toLowerCase()
      const map = {
        normal: '正常',
        official: '正式数据',
        vendor: '供应商数据',
        provisional: '临时数据',
        warning: '警告',
        degraded: '降级',
        error: '异常',
        failed: '异常',
        unavailable: '不可用',
        stale_running: '历史超时已收尾'
      }
      return map[value] || status || '暂不可用'
    },
    countText(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    payloadValue(row, key) {
      return row && row.machine_payload ? row.machine_payload[key] : ''
    },
    batchLogId(row) {
      if (!row) return '批次编号暂不可用'
      const payload = row.machine_payload || {}
      const locator = row.run_locator || {}
      return row.batch_log_id || row.log_id || locator.batch_log_id || payload.batch_log_id || '批次编号暂不可用'
    },
    runScopeText(row) {
      const groupId = this.parentGroupId(row)
      if (groupId === 'market_event_watch') return '系统自动管理范围'
      if (groupId === 'system_health_check') return '全系统'
      const scope = (row && row.matched_scope) || this.payloadValue(row, 'matched_scope')
      if (scope === 'formal_all') return '第一本账正式区'
      if (scope === 'first_book_all') return '第一本账全范围'
      if (scope === 'custom') return '自定义范围'
      return scope || '范围暂不可用'
    },
    runResultText(row) {
      const status = String((row && row.status) || '').toLowerCase()
      const dataStatus = String((row && row.data_status) || '').toLowerCase()
      if (status === 'running') return '正在执行'
      if (status === 'pending') return '等待执行'
      if (status === 'failed' || status === 'error') {
        return dataStatus === 'stale_running' ? '历史超时记录，已标记失败' : '执行失败'
      }

      if (row && row.task_type === 'system_health_check') {
        if (dataStatus === 'normal' || status === 'success') return '系统正常'
        if (dataStatus === 'warning') return '存在警告'
      }

      if (status === 'success') {
        const added = Number(row.new_items_count || 0)
        const deduped = Number(row.deduped_count || 0)
        if (added > 0 && deduped > 0) return `成功，新增 ${added} 条，重复过滤 ${deduped} 条`
        if (added > 0) return `成功，新增 ${added} 条`
        if (deduped > 0) return `成功，无新增，重复过滤 ${deduped} 条`
        return '成功，无新增'
      }
      return '结果暂不可用'
    },
    matchedSymbolsText(row) {
      if (!row) return '-'
      const payload = row.machine_payload || {}
      const list = row.matched_symbols || payload.matched_symbols || []
      if (!Array.isArray(list) || !list.length) return '-'
      return list.map(item => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          return [item.ts_code || item.code, item.name || item.stock_name].filter(Boolean).join(' ')
        }
        return String(item)
      }).join('，')
    },
    runTimeValue(row) {
      if (!row) return ''
      return row.run_time || row.completed_at || row.created_at_bj || row.created_at || ''
    },
    displayRunTime(row) {
      return this.displayTime(this.runTimeValue(row))
    },
    displayTime(value) {
      if (!value) return '-'
      const text = String(value).replace('T', ' ')
      return text.length > 19 ? text.slice(0, 19) : text
    }
  }
}
</script>

<style scoped>
.task-runs { max-width: 1560px; margin: 0 auto; }
.page-hero { min-height: 104px; padding: 22px 26px; border-radius: 18px; background: #f7faff; border: 1px solid #dbe7f5; display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.page-hero h1 { margin: 10px 0 6px; color: #0f172a; font-size: 28px; }
.page-hero p { margin: 0; color: #64748b; }
.scope-alert { margin-bottom: 14px; }
.panel { border-radius: 16px; margin-bottom: 14px; }
.contract-panel { background: #fbfdff; }
.contract-row { display: flex; justify-content: space-between; align-items: center; gap: 18px; }
.contract-row strong { color: #0f172a; }
.contract-row p { margin: 5px 0 0; color: #64748b; font-family: Consolas, Monaco, monospace; font-size: 12px; }
.contract-tags { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.filter-row { display: grid; grid-template-columns: 190px 140px minmax(330px, 1fr) 88px 88px; gap: 10px; align-items: center; }
.filter-note { margin: 10px 0 0; color: #7f8ea3; font-size: 12px; }
.panel-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.panel-head strong { color: #0f172a; }
.panel-head p { margin: 4px 0 0; color: #7f8ea3; font-size: 12px; }
.page-size-control { display: flex; align-items: center; gap: 7px; color: #64748b; font-size: 12px; }
.page-size-control .el-select { width: 72px; }
.full-id-cell, .detail-id { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #334155; font-family: Consolas, Monaco, monospace; }
.full-id-cell { min-width: max-content; white-space: nowrap; }
.full-id-cell > span { white-space: nowrap; word-break: normal; }
.detail-id { word-break: break-all; }
.pagination-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 16px; color: #64748b; font-size: 13px; }
.detail-body { padding: 0 22px 28px; }
.detail-descriptions { margin-top: 16px; }
.danger-text { color: #ef4444; }
@media (max-width: 1000px) {
  .filter-row { grid-template-columns: 1fr; }
  .page-hero, .contract-row, .panel-head, .pagination-row { flex-direction: column; align-items: flex-start; }
  .contract-tags { justify-content: flex-start; }
}
</style>
